/**
 * LRU localStorage cache for chat history.
 * Stale-while-revalidate: serve cached data instantly, refresh in background.
 *
 * Storage keys:
 *   modalx_cache_index        → { entries: [{ id, size, lastAccessed }] }
 *   modalx_cache_{historyId}  → { version, history, inputFiles, timestamp }
 */

const CACHE_VERSION = 1;
const MAX_CACHE_BYTES = 1 * 1024 * 1024; // 1MB
const INDEX_KEY = "modalx_cache_index";
const ENTRY_PREFIX = "modalx_cache_";

function getIndex() {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.entries)) return parsed;
    }
  } catch {}
  return { entries: [] };
}

function saveIndex(index) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch {}
}

function entryKey(historyId) {
  return ENTRY_PREFIX + historyId;
}

function totalSize(index) {
  return index.entries.reduce((sum, e) => sum + (e.size || 0), 0);
}

/**
 * Evict oldest entries until enough space is freed for newSize bytes.
 */
function evictIfNeeded(index, newSize) {
  // Sort by lastAccessed ascending (oldest first)
  index.entries.sort((a, b) => a.lastAccessed - b.lastAccessed);

  while (totalSize(index) + newSize > MAX_CACHE_BYTES && index.entries.length > 0) {
    const oldest = index.entries.shift();
    try {
      localStorage.removeItem(entryKey(oldest.id));
    } catch {}
  }
}

/**
 * Get cached history for a chat.
 * Returns { history, inputFiles } or null on miss.
 */
export function getCachedHistory(historyId) {
  if (!historyId) return null;

  try {
    const raw = localStorage.getItem(entryKey(historyId));
    if (!raw) return null;

    const entry = JSON.parse(raw);
    if (entry.version !== CACHE_VERSION) {
      // Stale format — treat as miss and clean up
      localStorage.removeItem(entryKey(historyId));
      return null;
    }

    // Update lastAccessed in index
    const index = getIndex();
    const indexEntry = index.entries.find((e) => String(e.id) === String(historyId));
    if (indexEntry) {
      indexEntry.lastAccessed = Date.now();
      saveIndex(index);
    }

    return {
      history: entry.history,
      inputFiles: entry.inputFiles,
    };
  } catch {
    return null;
  }
}

/**
 * Cache history data. Handles LRU eviction if over 1MB.
 */
export function setCachedHistory(historyId, history, inputFiles) {
  if (!historyId) return;

  try {
    const entry = {
      version: CACHE_VERSION,
      history,
      inputFiles: inputFiles || [],
      timestamp: Date.now(),
    };
    const serialized = JSON.stringify(entry);
    const size = serialized.length * 2; // rough byte estimate (UTF-16)

    // Don't cache entries larger than the entire budget
    if (size > MAX_CACHE_BYTES) return;

    const index = getIndex();

    // Remove existing entry for this id if present
    const existingIdx = index.entries.findIndex((e) => String(e.id) === String(historyId));
    if (existingIdx !== -1) {
      index.entries.splice(existingIdx, 1);
      try {
        localStorage.removeItem(entryKey(historyId));
      } catch {}
    }

    // Evict old entries if needed
    evictIfNeeded(index, size);

    // Write entry
    localStorage.setItem(entryKey(historyId), serialized);

    // Update index
    index.entries.push({
      id: historyId,
      size,
      lastAccessed: Date.now(),
    });
    saveIndex(index);
  } catch (e) {
    // localStorage full or unavailable — silently fail
    console.warn("historyCache: failed to write cache", e);
  }
}

/**
 * Invalidate (remove) a single cache entry.
 */
export function invalidateCache(historyId) {
  if (!historyId) return;

  try {
    localStorage.removeItem(entryKey(historyId));

    const index = getIndex();
    index.entries = index.entries.filter((e) => String(e.id) !== String(historyId));
    saveIndex(index);
  } catch {}
}

/**
 * Clear all cached history entries.
 */
export function clearAllCache() {
  try {
    const index = getIndex();
    for (const entry of index.entries) {
      localStorage.removeItem(entryKey(entry.id));
    }
    localStorage.removeItem(INDEX_KEY);
  } catch {}
}
